/*
 * Area "people": users (employees) and the profile page.
 * Owned by the wave-2 "people" agent; see TRANSLATING.md, "Wave 2 partition".
 *
 * One entry per key: key: { en: '...', fr: '...', es: '...' }.
 * New keys are people_<screen>_<name>, or the Android key from the translation memory
 * when the English and the meaning are the same (then copy its fr/es verbatim).
 * Look in common.js first. en is required; a missing fr/es falls back to English.
 */
export default {
  // ---- Reused from the Android translation memory
  employee_add_title: { en: 'Add User', fr: 'Ajouter un utilisateur', es: 'Añadir usuario' },
  employee_invite_title: { en: 'Invite User', fr: 'Inviter un utilisateur', es: 'Invitar usuario' },
  labelEmployeeId: { en: 'Employee ID', fr: "N° d'employé", es: 'ID de empleado' },
  user_required_first_name: { en: 'First Name', fr: 'Prénom', es: 'Nombre' },
  user_required_last_name: { en: 'Last Name', fr: 'Nom', es: 'Apellido' },
  user_address_title: { en: 'Address Information', fr: 'Adresse', es: 'Dirección' },
  user_license_expiry: { en: 'Expiry Date', fr: "Date d'expiration", es: 'Fecha de vencimiento' },
  app_employee: { en: 'Employee', fr: 'Employé', es: 'Empleado' },
  user_resend_confirm_button: { en: 'Send Invite', fr: "Envoyer l'invitation", es: 'Enviar invitación' },
  user_reset_button: { en: 'Reset', fr: 'Réinitialiser', es: 'Restablecer' },
  user_profile: { en: 'Profile', fr: 'Profil', es: 'Perfil' },
  company_change: { en: 'Change', fr: 'Changer', es: 'Cambiar' },
  about_title: { en: 'About', fr: 'À propos', es: 'Acerca de' },
  about_version: { en: 'Version', fr: 'Version', es: 'Versión' },
  about_copyright: { en: 'Copyright', fr: "Droits d'auteur", es: 'Derechos de autor' },
  actions_go: { en: 'Go', fr: 'Aller', es: 'Ir' },
  company_select_title: { en: 'Select a Company', fr: 'Choisir une entreprise', es: 'Seleccione una empresa' },
  company_select_select_button: { en: 'Select', fr: 'Choisir', es: 'Elegir' },
  password_change_current_password_label: { en: 'Current Password', fr: 'Mot de passe actuel', es: 'Contraseña actual' },
  password_change_new_password_label: { en: 'New Password', fr: 'Nouveau mot de passe', es: 'Nueva contraseña' },
  password_change_confirm_password_label: { en: 'Confirm New Password', fr: 'Confirmer le nouveau mot de passe', es: 'Confirmar nueva contraseña' },
  dailylog_not_available: { en: 'N/A', fr: 'S. O.', es: 'N/D' },

  // ---- Shared by several user screens
  people_document_title: { en: 'Users - ReadyChek', fr: 'Utilisateurs - ReadyChek', es: 'Usuarios - ReadyChek' },
  people_not_set: { en: 'Not Set', fr: 'Non défini', es: 'Sin definir' },
  people_global: { en: 'Global', fr: 'Global', es: 'Global' },
  people_global_with_value: { en: 'Global ({0})', fr: 'Global ({0})', es: 'Global ({0})' },
  people_license_expired: { en: 'Expired', fr: 'Expiré', es: 'Vencida' },
  people_not_receiving_email: { en: 'Not receiving email', fr: 'Ne reçoit pas les courriels', es: 'No recibe correos' },
  people_not_receiving_email_since: { en: 'Since {0}', fr: 'Depuis le {0}', es: 'Desde el {0}' },
  people_not_receiving_email_off: { en: 'Inspection emails to this user have been turned off.', fr: 'L’envoi des inspections à cet utilisateur a été désactivé.', es: 'Se desactivó el envío de inspecciones a este usuario.' },
  people_not_receiving_email_reason_hardbounce: { en: 'ReadyChek tried to send an email to this user, but the email address does not exist. Please check the spelling of the address and confirm with the user that it is correct.', fr: 'ReadyChek a tenté d’envoyer un courriel à cet utilisateur, mais l’adresse courriel n’existe pas. Veuillez vérifier l’orthographe de l’adresse et confirmer avec l’utilisateur qu’elle est exacte.', es: 'ReadyChek intentó enviar un correo a este usuario, pero la dirección de correo no existe. Verifique la ortografía de la dirección y confirme con el usuario que sea correcta.' },
  people_not_receiving_email_reason_spamcomplaint: { en: 'This user marked a ReadyChek email as spam, so ReadyChek has stopped sending email to this address. Please ask the user to mark ReadyChek as Not spam, then contact ReadyChek support to turn email back on.', fr: 'Cet utilisateur a signalé un courriel de ReadyChek comme pourriel; ReadyChek a donc cessé d’envoyer des courriels à cette adresse. Veuillez demander à l’utilisateur de marquer ReadyChek comme « Pas un pourriel », puis contactez le soutien ReadyChek pour réactiver les courriels.', es: 'Este usuario marcó un correo de ReadyChek como spam, por lo que ReadyChek dejó de enviar correos a esta dirección. Pida al usuario que marque ReadyChek como «No es spam» y luego contacte al soporte de ReadyChek para reactivar los correos.' },
  people_not_receiving_email_reason_blocked: { en: 'ReadyChek tried to send an email to this user, but the user’s email provider blocked it. Please ask the user to add ReadyChek to their safe senders list, or to give you a different email address.', fr: 'ReadyChek a tenté d’envoyer un courriel à cet utilisateur, mais son fournisseur de courriel l’a bloqué. Veuillez demander à l’utilisateur d’ajouter ReadyChek à sa liste d’expéditeurs approuvés ou de vous donner une autre adresse courriel.', es: 'ReadyChek intentó enviar un correo a este usuario, pero su proveedor de correo lo bloqueó. Pida al usuario que agregue ReadyChek a su lista de remitentes seguros o que le dé otra dirección de correo.' },
  people_not_receiving_email_reason_dnserror: { en: 'ReadyChek tried to send an email to this user, but the part of the address after the @ does not exist. Please check the spelling of the address and confirm with the user that it is correct.', fr: 'ReadyChek a tenté d’envoyer un courriel à cet utilisateur, mais la partie de l’adresse après le @ n’existe pas. Veuillez vérifier l’orthographe de l’adresse et confirmer avec l’utilisateur qu’elle est exacte.', es: 'ReadyChek intentó enviar un correo a este usuario, pero la parte de la dirección después de la @ no existe. Verifique la ortografía de la dirección y confirme con el usuario que sea correcta.' },
  people_not_receiving_email_reason_manuallydeactivated: { en: 'ReadyChek support has stopped sending email to this user. Please contact ReadyChek support to turn email back on.', fr: 'Le soutien ReadyChek a cessé d’envoyer des courriels à cet utilisateur. Veuillez contacter le soutien ReadyChek pour réactiver les courriels.', es: 'El soporte de ReadyChek dejó de enviar correos a este usuario. Contacte al soporte de ReadyChek para reactivar los correos.' },
  people_not_receiving_email_reason_inactiverecipient: { en: 'ReadyChek tried to send an email to this user, but it was not sent because of an earlier delivery problem or spam report. Please confirm the address with the user. If it is wrong, correct it; if it is correct, contact ReadyChek support to turn email back on.', fr: 'ReadyChek a tenté d’envoyer un courriel à cet utilisateur, mais il n’a pas été envoyé à cause d’un problème de livraison ou d’un signalement de pourriel antérieur. Veuillez confirmer l’adresse avec l’utilisateur. Si elle est erronée, corrigez-la; si elle est exacte, contactez le soutien ReadyChek pour réactiver les courriels.', es: 'ReadyChek intentó enviar un correo a este usuario, pero no se envió por un problema de entrega o un reporte de spam anterior. Confirme la dirección con el usuario. Si es incorrecta, corríjala; si es correcta, contacte al soporte de ReadyChek para reactivar los correos.' },
  people_not_receiving_email_reason_other: { en: 'ReadyChek tried to send an email to this user, but it could not be delivered. Please check the spelling of the address and confirm with the user that it is correct.', fr: 'ReadyChek a tenté d’envoyer un courriel à cet utilisateur, mais il n’a pas pu être livré. Veuillez vérifier l’orthographe de l’adresse et confirmer avec l’utilisateur qu’elle est exacte.', es: 'ReadyChek intentó enviar un correo a este usuario, pero no se pudo entregar. Verifique la ortografía de la dirección y confirme con el usuario que sea correcta.' },
  people_license_expires_in_days: { en: 'Expires in {0} days', fr: 'Expire dans {0} jours', es: 'Vence en {0} días' },
  people_license_class: { en: 'License Class', fr: 'Classe de permis', es: 'Clase de licencia' },
  people_phone_no: { en: 'Phone No.', fr: 'N° de téléphone', es: 'N.º de teléfono' },
  people_user_details: { en: 'User Details', fr: "Détails de l'utilisateur", es: 'Detalles del usuario' },
  people_hq_hint: {
    en: "If the user takes a vehicle home, this provides an 'HQ' button in the app.",
    fr: "Si l'utilisateur ramène un véhicule à la maison, un bouton « Siège » s'affiche dans l'application.",
    es: "Si el usuario se lleva un vehículo a casa, esto añade un botón 'Sede' en la aplicación.",
  },
  people_user_settings: { en: 'User Settings (Preferences)', fr: "Paramètres de l'utilisateur (préférences)", es: 'Ajustes del usuario (preferencias)' },
  people_allow_profile_updates: { en: 'Allow Profile Updates', fr: 'Autoriser la mise à jour du profil', es: 'Permitir actualizar el perfil' },
  people_allow_profile_updates_hint: {
    en: 'Allows user to update their own contact and license info.',
    fr: "Permet à l'utilisateur de mettre à jour ses propres coordonnées et renseignements de permis.",
    es: 'Permite al usuario actualizar sus propios datos de contacto y de licencia.',
  },
  people_allow_profile_updates_hint_details: {
    en: 'Allows this user to update their own contact and license info.',
    fr: 'Permet à cet utilisateur de mettre à jour ses propres coordonnées et renseignements de permis.',
    es: 'Permite a este usuario actualizar sus propios datos de contacto y de licencia.',
  },
  people_drivers_receive_email: { en: 'Drivers Receive Inspection Email', fr: "Courriel d'inspection aux conducteurs", es: 'Los conductores reciben el correo de inspección' },
  people_drivers_receive_email_hint: {
    en: "Sends a PDF copy of each inspection to the driver's email.",
    fr: 'Envoie une copie PDF de chaque inspection au courriel du conducteur.',
    es: 'Envía una copia en PDF de cada inspección al correo electrónico del conductor.',
  },
  people_hos_mandatory: { en: 'Hours of Service Mandatory', fr: 'Heures de service obligatoires', es: 'Horas de servicio obligatorias' },
  people_hos_mandatory_hint: {
    en: 'Requires this user to start HOS before an inspection.',
    fr: 'Oblige cet utilisateur à commencer ses heures de service avant une inspection.',
    es: 'Exige que este usuario inicie sus horas de servicio antes de una inspección.',
  },
  people_map_in_app: { en: 'Show Map in Phone App', fr: "Afficher la carte dans l'application mobile", es: 'Mostrar el mapa en la aplicación móvil' },
  people_map_in_app_hint: {
    en: 'Shows the Map to this user in the phone app.',
    fr: "Affiche la carte à cet utilisateur dans l'application mobile.",
    es: 'Muestra el mapa a este usuario en la aplicación móvil.',
  },
  people_employment_dates: { en: 'Employment Dates', fr: "Dates d'emploi", es: 'Fechas de empleo' },
  people_role_employment: { en: 'Role & Employment', fr: 'Rôle et emploi', es: 'Rol y empleo' },
  people_employee_id_shared: { en: 'Employee ID (for shared devices)', fr: "N° d'employé (appareils partagés)", es: 'ID de empleado (dispositivos compartidos)' },
  people_notes: { en: 'Notes', fr: 'Notes', es: 'Notas' },
  people_drivers_license: { en: "Driver's License", fr: 'Permis de conduire', es: 'Licencia de conducir' },
  people_license_hint: {
    en: 'This will show a message when the license is about to expire.',
    fr: "Un message s'affichera lorsque le permis est sur le point d'expirer.",
    es: 'Se mostrará un mensaje cuando la licencia esté por vencer.',
  },
  people_saving_users: { en: 'Saving Users...', fr: 'Enregistrement des utilisateurs...', es: 'Guardando usuarios...' },
  people_processing: { en: 'Processing...', fr: 'Traitement...', es: 'Procesando...' },
  people_recipient_email: { en: 'Recipient Email', fr: 'Courriel du destinataire', es: 'Correo electrónico del destinatario' },
  people_fill_required: { en: 'Please fill in all required fields.', fr: 'Veuillez remplir tous les champs obligatoires.', es: 'Complete todos los campos obligatorios.' },
  people_move_to: { en: 'Move to:', fr: 'Déplacer vers :', es: 'Mover a:' },
  people_error_unknown: { en: 'An unknown error occurred.', fr: "Une erreur inconnue s'est produite.", es: 'Se produjo un error desconocido.' },
  people_error_load_user_company: {
    en: 'Could not load user or company data.',
    fr: "Impossible de charger les données de l'utilisateur ou de l'entreprise.",
    es: 'No se pudieron cargar los datos del usuario o de la empresa.',
  },
  people_error_no_employment_record: {
    en: "Unable to find this user's employment record.",
    fr: "Impossible de trouver le dossier d'emploi de cet utilisateur.",
    es: 'No se encuentra el registro de empleo de este usuario.',
  },
  people_error_no_employment_company: {
    en: 'Could not find a valid employment record for this user at this company.',
    fr: "Impossible de trouver un dossier d'emploi valide pour cet utilisateur dans cette entreprise.",
    es: 'No se encontró un registro de empleo válido para este usuario en esta empresa.',
  },
  people_not_found_details: {
    en: 'Could not find user or employee details.',
    fr: "Impossible de trouver les détails de l'utilisateur ou de l'employé.",
    es: 'No se encontraron los detalles del usuario o del empleado.',
  },

  // ---- Employee state (record state codes)
  people_state_invited: { en: 'Invited', fr: 'Invité', es: 'Invitado' },
  people_state_notinvited: { en: 'Not Invited', fr: 'Non invité', es: 'No invitado' },
  people_state_waiting: { en: 'Waiting to be Invited', fr: "En attente d'invitation", es: 'En espera de invitación' },
  people_state_aip: { en: 'Active, Invited or Waiting', fr: 'Actif, invité ou en attente', es: 'Activo, invitado o en espera' },
  people_state_removed: { en: 'Removed', fr: 'Retiré', es: 'Retirado' },

  // ---- Compliance type codes
  people_compliance_included: { en: 'included', fr: 'inclus', es: 'incluido' },
  people_compliance_excluded: { en: 'excluded', fr: 'exclu', es: 'excluido' },

  // ---- Users table (EmployeeDataTable)
  people_table_search: { en: 'Search Users', fr: 'Rechercher des utilisateurs', es: 'Buscar usuarios' },
  people_table_license_status: { en: 'License Status', fr: 'Statut du permis', es: 'Estado de la licencia' },
  people_table_activity: { en: 'Activity', fr: 'Activité', es: 'Actividad' },
  people_table_email_delivery: { en: 'Email Delivery', fr: 'Livraison des courriels', es: 'Entrega de correos' },
  people_table_license_expiry: { en: 'License Expiry', fr: 'Expiration du permis', es: 'Vencimiento de la licencia' },
  people_table_compliance_type: { en: 'Compliance Type', fr: 'Type de conformité', es: 'Tipo de cumplimiento' },
  people_table_last_sign_in: { en: 'Last Sign In', fr: 'Dernière connexion', es: 'Último inicio de sesión' },
  people_table_has_not_signed_in: { en: '[Has not signed in]', fr: '[Jamais connecté]', es: '[No ha iniciado sesión]' },
  people_table_label_none: { en: 'None', fr: 'Aucun', es: 'Ninguno' },
  people_role_owners: { en: 'Owners', fr: 'Propriétaires', es: 'Propietarios' },
  people_role_managers: { en: 'Managers', fr: 'Gestionnaires', es: 'Gerentes' },
  people_role_owners_managers: { en: 'Owners and Managers', fr: 'Propriétaires et gestionnaires', es: 'Propietarios y gerentes' },
  people_role_commercial_and_nc: {
    en: 'Commercial and Non Commercial Driver',
    fr: 'Conducteur commercial et non commercial',
    es: 'Conductor comercial y no comercial',
  },
  people_role_mechanics: { en: 'Mechanics', fr: 'Mécaniciens', es: 'Mecánicos' },
  people_license_expiring_soon: { en: 'Expiring Soon', fr: 'Expire bientôt', es: 'Vence pronto' },
  people_license_any: { en: 'Any', fr: 'Peu importe', es: 'Cualquiera' },
  people_activity_today: { en: 'Active Today', fr: "Actifs aujourd'hui", es: 'Activos hoy' },

  // ---- Email error (inline email edit in the table)
  people_email_error_title: { en: 'Error Saving Email', fr: "Erreur lors de l'enregistrement du courriel", es: 'Error al guardar el correo electrónico' },
  people_email_error_unknown: {
    en: 'An unknown error occurred while saving the email.',
    fr: "Une erreur inconnue s'est produite lors de l'enregistrement du courriel.",
    es: 'Se produjo un error desconocido al guardar el correo electrónico.',
  },

  // ---- Add / invite / update user (EmployeeUpdateDialog)
  people_update_title: { en: 'Update User: {0} {1}', fr: "Modifier l'utilisateur : {0} {1}", es: 'Actualizar usuario: {0} {1}' },
  people_add_new_user: { en: 'Add New User', fr: 'Ajouter un nouvel utilisateur', es: 'Añadir nuevo usuario' },
  people_invite_new_user: { en: 'Invite New User', fr: 'Inviter un nouvel utilisateur', es: 'Invitar a un nuevo usuario' },
  people_verify_email: { en: 'Verify Email', fr: 'Confirmer le courriel', es: 'Confirmar correo electrónico' },
  people_role_hint: {
    en: 'Manager/Owner can access this portal. All roles can use the app.',
    fr: "Les gestionnaires et propriétaires ont accès à ce portail. Tous les rôles peuvent utiliser l'application.",
    es: 'Los gerentes y propietarios pueden acceder a este portal. Todos los roles pueden usar la aplicación.',
  },
  people_send_invite_now: { en: 'Send invite email now', fr: "Envoyer le courriel d'invitation maintenant", es: 'Enviar el correo de invitación ahora' },
  people_rule_required: { en: 'Field is required', fr: 'Champ obligatoire', es: 'Campo obligatorio' },
  people_rule_email: { en: 'Must be a valid email.', fr: 'Doit être un courriel valide.', es: 'Debe ser un correo electrónico válido.' },
  people_rule_email_match: {
    en: 'Email addresses do not match.',
    fr: 'Les adresses courriel ne correspondent pas.',
    es: 'Las direcciones de correo electrónico no coinciden.',
  },
  people_error_no_employment: {
    en: 'Could not find a valid employment record for this user.',
    fr: "Impossible de trouver un dossier d'emploi valide pour cet utilisateur.",
    es: 'No se encontró un registro de empleo válido para este usuario.',
  },
  people_error_no_user_company: {
    en: 'Could not find user or company to update.',
    fr: "Impossible de trouver l'utilisateur ou l'entreprise à mettre à jour.",
    es: 'No se encontró el usuario o la empresa que desea actualizar.',
  },
  people_error_correct_form: {
    en: 'Please correct the errors in the form.',
    fr: 'Veuillez corriger les erreurs du formulaire.',
    es: 'Corrija los errores del formulario.',
  },
  people_error_email_taken: {
    en: "That email address is already on this company's employee list. If you cannot see the person, they are Inactive - clear the filter to show inactive users and restore them, rather than adding them again.",
    fr: "Cette adresse courriel figure déjà dans la liste des employés de cette entreprise. Si vous ne voyez pas la personne, elle est inactive : retirez le filtre pour afficher les utilisateurs inactifs et restaurez-la plutôt que de l'ajouter de nouveau.",
    es: 'Esa dirección de correo electrónico ya está en la lista de empleados de esta empresa. Si no ve a la persona, está inactiva: quite el filtro para mostrar los usuarios inactivos y restáurela en lugar de añadirla de nuevo.',
  },

  // ---- User details (EmployeeDetailsDialog)
  people_details_title: { en: 'Details for: {0}', fr: 'Détails de : {0}', es: 'Detalles de: {0}' },
  people_details_load_error: {
    en: 'User details could not be loaded.',
    fr: "Impossible de charger les détails de l'utilisateur.",
    es: 'No se pudieron cargar los detalles del usuario.',
  },

  // ---- Export (EmployeeExportDialog)
  people_export_title: { en: 'Export Users', fr: 'Exporter les utilisateurs', es: 'Exportar usuarios' },
  people_export_heading: { en: 'Export Users to CSV', fr: 'Exporter les utilisateurs en CSV', es: 'Exportar usuarios a CSV' },
  people_export_body: {
    en: 'This will export all users matching your current filters to a CSV file.',
    fr: 'Tous les utilisateurs correspondant à vos filtres actuels seront exportés dans un fichier CSV.',
    es: 'Se exportarán a un archivo CSV todos los usuarios que coincidan con sus filtros actuales.',
  },
  people_export_download_hint: {
    en: 'When the operation is finished, a download button will appear.',
    fr: "Une fois l'opération terminée, un bouton de téléchargement s'affichera.",
    es: 'Cuando termine la operación, aparecerá un botón de descarga.',
  },
  people_exporting: { en: 'Exporting Users...', fr: 'Exportation des utilisateurs...', es: 'Exportando usuarios...' },
  people_export_complete: { en: 'Export Complete!', fr: 'Exportation terminée!', es: '¡Exportación completada!' },
  people_export_press_download: {
    en: 'Press the Download button to get your file.',
    fr: 'Appuyez sur le bouton Télécharger pour obtenir votre fichier.',
    es: 'Pulse el botón Descargar para obtener su archivo.',
  },
  people_export_invalid_filter: { en: 'An invalid filter was provided.', fr: 'Un filtre non valide a été fourni.', es: 'Se proporcionó un filtro no válido.' },
  people_export_failed: { en: 'Failed to export users.', fr: "Échec de l'exportation des utilisateurs.", es: 'No se pudieron exportar los usuarios.' },

  // ---- Send / resend invite, reset password (EmployeeSendDialog, EmployeeResendDialog, EmployeeResetDialog)
  people_send_title: { en: 'Send Invite: {0}', fr: "Envoyer l'invitation : {0}", es: 'Enviar invitación: {0}' },
  people_send_confirm: {
    en: 'Are you sure you want to send an invite to this user?',
    fr: 'Voulez-vous vraiment envoyer une invitation à cet utilisateur?',
    es: '¿Está seguro de que desea enviar una invitación a este usuario?',
  },
  people_send_body: {
    en: 'This action will send an invitation email to the user with a temporary password.',
    fr: "Cette action enverra à l'utilisateur un courriel d'invitation avec un mot de passe temporaire.",
    es: 'Esta acción enviará al usuario un correo de invitación con una contraseña temporal.',
  },
  people_send_failed: { en: 'Failed to send the invite.', fr: "Échec de l'envoi de l'invitation.", es: 'No se pudo enviar la invitación.' },
  people_resend_title: { en: 'Resend Invite: {0}', fr: "Renvoyer l'invitation : {0}", es: 'Reenviar invitación: {0}' },
  people_resend_confirm: {
    en: 'Are you sure you want to resend the invite?',
    fr: "Voulez-vous vraiment renvoyer l'invitation?",
    es: '¿Está seguro de que desea reenviar la invitación?',
  },
  people_resend_body: {
    en: 'This action will send a new invitation email with a new password.',
    fr: "Cette action enverra un nouveau courriel d'invitation avec un nouveau mot de passe.",
    es: 'Esta acción enviará un nuevo correo de invitación con una nueva contraseña.',
  },
  people_resend_failed: { en: 'Failed to resend the invite.', fr: "Échec du renvoi de l'invitation.", es: 'No se pudo reenviar la invitación.' },
  people_reset_title: { en: 'Reset Password: {0}', fr: 'Réinitialiser le mot de passe : {0}', es: 'Restablecer contraseña: {0}' },
  people_reset_confirm: {
    en: "Are you sure you want to reset this user's password?",
    fr: 'Voulez-vous vraiment réinitialiser le mot de passe de cet utilisateur?',
    es: '¿Está seguro de que desea restablecer la contraseña de este usuario?',
  },
  people_reset_body: {
    en: "This action will change the user's password and send them an email with the new one.",
    fr: "Cette action changera le mot de passe de l'utilisateur et lui enverra un courriel avec le nouveau.",
    es: 'Esta acción cambiará la contraseña del usuario y le enviará un correo electrónico con la nueva.',
  },
  people_reset_failed: { en: 'Failed to reset the password.', fr: 'Échec de la réinitialisation du mot de passe.', es: 'No se pudo restablecer la contraseña.' },

  // ---- Restore / remove / move one user
  people_restore_title: { en: 'Restore User: {0}', fr: "Restaurer l'utilisateur : {0}", es: 'Restaurar usuario: {0}' },
  people_restore_confirm: {
    en: 'Are you sure you want to restore this user?',
    fr: 'Voulez-vous vraiment restaurer cet utilisateur?',
    es: '¿Está seguro de que desea restaurar a este usuario?',
  },
  people_restore_body: {
    en: 'This will restore the user and allow them to sign in with their previous password.',
    fr: "Cela restaurera l'utilisateur et lui permettra de se connecter avec son mot de passe précédent.",
    es: 'Esto restaurará al usuario y le permitirá iniciar sesión con su contraseña anterior.',
  },
  people_restore_failed: { en: 'Failed to restore the user.', fr: "Échec de la restauration de l'utilisateur.", es: 'No se pudo restaurar al usuario.' },
  people_remove_title: { en: 'Remove User: {0}', fr: "Retirer l'utilisateur : {0}", es: 'Quitar usuario: {0}' },
  people_remove_confirm: {
    en: 'Are you sure you want to remove this user?',
    fr: 'Voulez-vous vraiment retirer cet utilisateur?',
    es: '¿Está seguro de que desea quitar a este usuario?',
  },
  people_remove_body: {
    en: 'This action will make the user Inactive, and they will no longer be able to use the app or web portal for this company.',
    fr: "Cette action rendra l'utilisateur inactif; il ne pourra plus utiliser l'application ni le portail Web pour cette entreprise.",
    es: 'Esta acción dejará al usuario inactivo y ya no podrá usar la aplicación ni el portal web de esta empresa.',
  },
  people_remove_recommend: {
    en: 'This is recommended if the user no longer works for the company.',
    fr: "Recommandé si l'utilisateur ne travaille plus pour l'entreprise.",
    es: 'Se recomienda si el usuario ya no trabaja para la empresa.',
  },
  people_remove_failed: { en: 'Failed to remove the user.', fr: "Échec du retrait de l'utilisateur.", es: 'No se pudo quitar al usuario.' },
  people_move_title: { en: 'Move User: {0}', fr: "Déplacer l'utilisateur : {0}", es: 'Mover usuario: {0}' },
  people_move_confirm: {
    en: 'Are you sure you want to move this User to a different division?',
    fr: 'Voulez-vous vraiment déplacer cet utilisateur vers une autre division?',
    es: '¿Está seguro de que desea mover a este usuario a otra división?',
  },
  people_move_body: {
    en: 'This action will move this User to another division',
    fr: 'Cette action déplacera cet utilisateur vers une autre division',
    es: 'Esta acción moverá a este usuario a otra división',
  },
  people_move_failed: { en: 'Failed to move the employee.', fr: "Échec du déplacement de l'employé.", es: 'No se pudo mover al empleado.' },
  people_user_not_found: { en: 'User not found.', fr: 'Utilisateur introuvable.', es: 'Usuario no encontrado.' },
  // EmployeeSearchDialog, laid out like CompanySearchDialog.
  employee_search_title: { en: 'Search Employees', fr: 'Rechercher des employés', es: 'Buscar empleados' },
  employee_search_label: { en: 'Search by name or email', fr: 'Rechercher par nom ou courriel', es: 'Buscar por nombre o correo' },
  employee_search_none: { en: 'No employees found', fr: 'Aucun employé trouvé', es: 'No se encontraron empleados' },
  employee_search_more: {
    en: 'More employees match. Type more of the name to narrow the list.',
    fr: "D'autres employés correspondent. Tapez davantage du nom pour réduire la liste.",
    es: 'Hay más empleados que coinciden. Escriba más del nombre para acotar la lista.',
  },
  people_select_user: { en: 'Select a User', fr: 'Choisir un utilisateur', es: 'Seleccione un usuario' },

  // ---- Import (EmployeeImportDialog). Column names stay English: they are the CSV headers.
  people_import_title: { en: 'Import Users from File', fr: "Importer des utilisateurs à partir d'un fichier", es: 'Importar usuarios desde un archivo' },
  people_import_workflow: { en: 'User Import Workflow', fr: "Étapes d'importation des utilisateurs", es: 'Proceso de importación de usuarios' },
  people_import_intro: {
    en: 'Follow these steps to import users from a CSV file.',
    fr: "Suivez ces étapes pour importer des utilisateurs à partir d'un fichier CSV.",
    es: 'Siga estos pasos para importar usuarios desde un archivo CSV.',
  },
  people_import_step1: { en: '1. Download Template', fr: '1. Télécharger le modèle', es: '1. Descargar la plantilla' },
  people_import_step1_hint: { en: 'Get the CSV template file.', fr: 'Obtenez le fichier modèle CSV.', es: 'Obtenga el archivo de plantilla CSV.' },
  people_import_step2: {
    en: '2. Fill In Template (Show Instructions)',
    fr: '2. Remplir le modèle (afficher les instructions)',
    es: '2. Completar la plantilla (mostrar instrucciones)',
  },
  people_import_required_fields: {
    en: 'Please make sure to fill in all the required fields.',
    fr: 'Assurez-vous de remplir tous les champs obligatoires.',
    es: 'Asegúrese de completar todos los campos obligatorios.',
  },
  people_import_mandatory: {
    en: 'Any label surrounded by **...** is a mandatory field and the import will fail if not filled in for every employee.',
    fr: "Toute étiquette entourée de **...** est un champ obligatoire; l'importation échouera s'il n'est pas rempli pour chaque employé.",
    es: 'Toda etiqueta rodeada de **...** es un campo obligatorio y la importación fallará si no se completa para cada empleado.',
  },
  people_import_help_rows: {
    en: "The template's first two rows start with HELP: and explain each column and the values it accepts. You can leave them in; they are skipped on import.",
    fr: "Les deux premières lignes du modèle commencent par HELP: et expliquent chaque colonne et les valeurs acceptées. Vous pouvez les laisser; elles sont ignorées à l'importation.",
    es: 'Las dos primeras filas de la plantilla empiezan con HELP: y explican cada columna y los valores que acepta. Puede dejarlas; se omiten al importar.',
  },
  people_import_col_first_name: { en: "The employee's first name.", fr: "Le prénom de l'employé.", es: 'El nombre del empleado.' },
  people_import_col_email: {
    en: "The employee's email address. The invitation is sent here.",
    fr: "L'adresse courriel de l'employé. L'invitation y est envoyée.",
    es: 'La dirección de correo electrónico del empleado. La invitación se envía a esta dirección.',
  },
  people_import_col_last_name: { en: "The employee's last name.", fr: "Le nom de famille de l'employé.", es: 'El apellido del empleado.' },
  people_import_col_phone: { en: "The employee's phone number.", fr: "Le numéro de téléphone de l'employé.", es: 'El número de teléfono del empleado.' },
  people_import_col_street: { en: "The employee's street address.", fr: "L'adresse municipale de l'employé.", es: 'La dirección del empleado.' },
  people_import_col_city: { en: "The employee's city.", fr: "La ville de l'employé.", es: 'La ciudad del empleado.' },
  people_import_col_province: { en: 'The province or state.', fr: "La province ou l'État.", es: 'La provincia o el estado.' },
  people_import_col_country: {
    en: "'Canada'/'CA' or 'United States'/'US'. (Default: 'Canada')",
    fr: "'Canada'/'CA' ou 'United States'/'US'. (Par défaut : 'Canada')",
    es: "'Canada'/'CA' o 'United States'/'US'. (Predeterminado: 'Canada')",
  },
  people_import_col_postal: { en: 'The postal or ZIP code.', fr: 'Le code postal ou ZIP.', es: 'El código postal o ZIP.' },
  people_import_col_role: {
    en: "'Driver', 'Non-Commercial', 'Manager' or 'Owner'. (Default: 'Driver')",
    fr: "'Driver', 'Non-Commercial', 'Manager' ou 'Owner'. (Par défaut : 'Driver')",
    es: "'Driver', 'Non-Commercial', 'Manager' u 'Owner'. (Predeterminado: 'Driver')",
  },
  people_import_col_employee_id: {
    en: 'Your own ID or number for the employee.',
    fr: "Votre propre identifiant ou numéro pour l'employé.",
    es: 'Su propio identificador o número para el empleado.',
  },
  people_import_col_license_class: {
    en: "e.g., 'G', 'AZ', 'DZ'. (Default: 'G')",
    fr: "p. ex. 'G', 'AZ', 'DZ'. (Par défaut : 'G')",
    es: "p. ej., 'G', 'AZ', 'DZ'. (Predeterminado: 'G')",
  },
  people_import_col_date_format: { en: 'YYYY-MM-DD or YYYY/MM/DD.', fr: 'AAAA-MM-JJ ou AAAA/MM/JJ.', es: 'AAAA-MM-DD o AAAA/MM/DD.' },
  people_import_col_state: {
    en: 'Filled in on export. Not used on import.',
    fr: "Rempli à l'exportation. Non utilisé à l'importation.",
    es: 'Se completa al exportar. No se usa al importar.',
  },
  people_import_step3: { en: '3. Upload the CSV File', fr: '3. Téléverser le fichier CSV', es: '3. Subir el archivo CSV' },
  people_import_select_file: { en: 'Select CSV file', fr: 'Choisir le fichier CSV', es: 'Seleccione el archivo CSV' },
  people_import_step4: { en: '4. Click Import', fr: '4. Cliquer sur Importer', es: '4. Haga clic en Importar' },
  people_import_dont_close: {
    en: 'Please do not close the window or refresh the page while the import is in progress.',
    fr: "Veuillez ne pas fermer la fenêtre ni actualiser la page pendant l'importation.",
    es: 'No cierre la ventana ni actualice la página mientras la importación esté en curso.',
  },
  people_importing: { en: 'Importing Users...', fr: 'Importation des utilisateurs...', es: 'Importando usuarios...' },
  people_import_review: { en: 'Users Ready for Review', fr: 'Utilisateurs prêts à vérifier', es: 'Usuarios listos para revisar' },
  people_import_review_body: {
    en: 'Please look over the employees you have uploaded. Click "Save Users" to finalize the import or "Start Over" to upload an updated file.',
    fr: "Veuillez vérifier les employés téléversés. Cliquez sur « Enregistrer les utilisateurs » pour terminer l'importation ou sur « Recommencer » pour téléverser un fichier à jour.",
    es: 'Revise los empleados que ha subido. Haga clic en "Guardar usuarios" para finalizar la importación o en "Empezar de nuevo" para subir un archivo actualizado.',
  },
  people_import_start_over: { en: 'Start Over', fr: 'Recommencer', es: 'Empezar de nuevo' },
  people_import_save_users: { en: 'Save Users', fr: 'Enregistrer les utilisateurs', es: 'Guardar usuarios' },
  people_import_template_failed: { en: 'Failed to get template.', fr: "Impossible d'obtenir le modèle.", es: 'No se pudo obtener la plantilla.' },
  people_import_read_data_failed: { en: 'Could not read file data.', fr: 'Impossible de lire les données du fichier.', es: 'No se pudieron leer los datos del archivo.' },
  people_import_no_file: { en: 'Please select a file to upload.', fr: 'Veuillez choisir un fichier à téléverser.', es: 'Seleccione un archivo para subir.' },
  people_import_reading: { en: 'Reading file...', fr: 'Lecture du fichier...', es: 'Leyendo el archivo...' },
  people_import_uploading: { en: 'Uploading file...', fr: 'Téléversement du fichier...', es: 'Subiendo el archivo...' },
  people_import_read_failed: { en: 'Failed to read file for import.', fr: "Impossible de lire le fichier pour l'importation.", es: 'No se pudo leer el archivo para importar.' },
  people_import_failed: { en: 'Failed to import file.', fr: "Échec de l'importation du fichier.", es: 'No se pudo importar el archivo.' },
  people_import_none_found: {
    en: 'No users were found in the imported file.',
    fr: "Aucun utilisateur n'a été trouvé dans le fichier importé.",
    es: 'No se encontraron usuarios en el archivo importado.',
  },
  people_import_saving_progress: { en: 'Saving employee {0} of {1}', fr: "Enregistrement de l'employé {0} sur {1}", es: 'Guardando empleado {0} de {1}' },
  people_import_save_failed: {
    en: 'Failed to save employee {0} ({1}): {2}',
    fr: "Échec de l'enregistrement de l'employé {0} ({1}) : {2}",
    es: 'No se pudo guardar el empleado {0} ({1}): {2}',
  },

  // ---- Bulk actions (EmployeeBulkActionDialog)
  people_bulk_title_move: { en: 'Move Users', fr: 'Déplacer les utilisateurs', es: 'Mover usuarios' },
  people_bulk_title_restore: { en: 'Restore Users', fr: 'Restaurer les utilisateurs', es: 'Restaurar usuarios' },
  people_bulk_title_remove: { en: 'Remove Users', fr: 'Retirer les utilisateurs', es: 'Quitar usuarios' },
  people_bulk_title_label: { en: 'Set Group for Users', fr: 'Définir le groupe des utilisateurs', es: 'Asignar grupo a los usuarios' },
  people_bulk_title_license: {
    en: 'Set License Expiry for Users',
    fr: "Définir l'expiration du permis des utilisateurs",
    es: 'Establecer el vencimiento de la licencia de los usuarios',
  },
  // {0} is one of the people_bulk_verb_* below, shown in bold.
  people_bulk_confirm: {
    en: 'Are you sure you want to {0} these users?',
    fr: 'Voulez-vous vraiment {0} ces utilisateurs?',
    es: '¿Está seguro de que desea {0} estos usuarios?',
  },
  people_bulk_verb_move: { en: 'move', fr: 'déplacer', es: 'mover a' },
  people_bulk_verb_restore: { en: 'restore', fr: 'restaurer', es: 'restaurar a' },
  people_bulk_verb_remove: { en: 'remove', fr: 'retirer', es: 'quitar a' },
  people_bulk_verb_label: { en: 'set the location for', fr: "définir l'emplacement de", es: 'definir la ubicación de' },
  people_bulk_verb_license: {
    en: "set the driver's license expiry for",
    fr: "définir l'expiration du permis de conduire de",
    es: 'establecer el vencimiento de la licencia de conducir de',
  },
  people_bulk_users_affected: { en: 'Users affected ({0}):', fr: 'Utilisateurs concernés ({0}) :', es: 'Usuarios afectados ({0}):' },
  people_bulk_apply_labels: { en: 'Apply label(s):', fr: 'Appliquer les étiquettes :', es: 'Aplicar etiquetas:' },
  people_bulk_select_date: { en: 'Select Date:', fr: 'Choisir la date :', es: 'Seleccione la fecha:' },
  people_bulk_clear_date: { en: 'Clear Date', fr: 'Effacer la date', es: 'Borrar fecha' },
  people_bulk_one_year: { en: '1 Year from Today', fr: "1 an à partir d'aujourd'hui", es: '1 año a partir de hoy' },
  people_bulk_sub_move: {
    en: 'This action will move the users to another division.',
    fr: 'Cette action déplacera les utilisateurs vers une autre division.',
    es: 'Esta acción moverá a los usuarios a otra división.',
  },
  people_bulk_sub_restore: {
    en: 'This action will make the users Active again, and they will reappear in the app.',
    fr: "Cette action rendra les utilisateurs de nouveau actifs; ils réapparaîtront dans l'application.",
    es: 'Esta acción volverá a activar a los usuarios y reaparecerán en la aplicación.',
  },
  people_bulk_sub_remove: {
    en: 'This action will make the users Inactive, and they will no longer appear in the app.',
    fr: "Cette action rendra les utilisateurs inactifs; ils n'apparaîtront plus dans l'application.",
    es: 'Esta acción dejará inactivos a los usuarios y ya no aparecerán en la aplicación.',
  },
  people_bulk_sub_label: {
    en: 'This action will apply the location(s) to all selected users.',
    fr: 'Cette action appliquera les emplacements à tous les utilisateurs sélectionnés.',
    es: 'Esta acción aplicará las ubicaciones a todos los usuarios seleccionados.',
  },
  people_bulk_sub_license: {
    en: "This action will update the driver's license expiry date for all selected users.",
    fr: "Cette action mettra à jour la date d'expiration du permis de conduire de tous les utilisateurs sélectionnés.",
    es: 'Esta acción actualizará la fecha de vencimiento de la licencia de conducir de todos los usuarios seleccionados.',
  },
  people_bulk_sub_default: { en: 'This action will operate on this Item', fr: "Cette action s'appliquera à cet élément", es: 'Esta acción se aplicará a este elemento' },
  people_bulk_progress_move: { en: 'Moving users {0} of {1}...', fr: 'Déplacement des utilisateurs {0} sur {1}...', es: 'Moviendo usuarios {0} de {1}...' },
  people_bulk_progress_restore: { en: 'Restoring users {0} of {1}...', fr: 'Restauration des utilisateurs {0} sur {1}...', es: 'Restaurando usuarios {0} de {1}...' },
  people_bulk_progress_remove: { en: 'Removing users {0} of {1}...', fr: 'Retrait des utilisateurs {0} sur {1}...', es: 'Quitando usuarios {0} de {1}...' },
  people_bulk_progress_label: {
    en: 'Setting Location users {0} of {1}...',
    fr: "Définition de l'emplacement des utilisateurs {0} sur {1}...",
    es: 'Asignando ubicación a usuarios {0} de {1}...',
  },
  people_bulk_progress_license: {
    en: 'Setting license expiry on users {0} of {1}...',
    fr: "Définition de l'expiration du permis des utilisateurs {0} sur {1}...",
    es: 'Estableciendo el vencimiento de la licencia de usuarios {0} de {1}...',
  },
  people_bulk_progress_default: { en: 'users {0} of {1}...', fr: 'utilisateurs {0} sur {1}...', es: 'usuarios {0} de {1}...' },
  people_bulk_failures: {
    en: 'Completed with {0} failure(s). Please try again.',
    fr: 'Terminé avec {0} échec(s). Veuillez réessayer.',
    es: 'Completado con {0} error(es). Inténtelo de nuevo.',
  },
  people_bulk_load_failed: {
    en: 'Could not load selected users data.',
    fr: 'Impossible de charger les données des utilisateurs sélectionnés.',
    es: 'No se pudieron cargar los datos de los usuarios seleccionados.',
  },

  // ---- Profile page (ProfileView)
  people_profile_document_title: { en: 'Profile - ReadyChek', fr: 'Profil - ReadyChek', es: 'Perfil - ReadyChek' },
  people_profile_current_company: { en: 'Currently active company', fr: 'Entreprise active', es: 'Empresa activa actualmente' },
  people_profile_leave_review: { en: 'Leave a review', fr: 'Laisser un avis', es: 'Dejar una reseña' },
  people_profile_text_size_hint: { en: 'Makes the portal text larger or smaller on this browser', fr: 'Agrandit ou réduit le texte du portail sur ce navigateur', es: 'Agranda o reduce el texto del portal en este navegador' },
  people_profile_text_size_smaller: { en: 'Smaller text', fr: 'Texte plus petit', es: 'Texto más pequeño' },
  people_profile_text_size_larger: { en: 'Larger text', fr: 'Texte plus grand', es: 'Texto más grande' },
  people_profile_app_settings: { en: 'App Settings', fr: "Paramètres de l'application", es: 'Ajustes de la aplicación' },
  people_profile_theme: { en: 'Theme', fr: 'Thème', es: 'Tema' },
  people_profile_theme_hint: {
    en: 'Toggle between light and dark mode.',
    fr: 'Basculer entre le mode clair et le mode sombre.',
    es: 'Alterne entre el modo claro y el oscuro.',
  },
  people_profile_light: { en: 'Light', fr: 'Clair', es: 'Claro' },
  people_profile_dark: { en: 'Dark', fr: 'Sombre', es: 'Oscuro' },
  people_profile_legacy: { en: 'Legacy Interface', fr: 'Ancienne interface', es: 'Interfaz anterior' },
  people_profile_legacy_hint: {
    en: 'Switch back to the old interface of ReadyChek.',
    fr: "Revenir à l'ancienne interface de ReadyChek.",
    es: 'Vuelva a la interfaz anterior de ReadyChek.',
  },
  people_profile_switch_back: { en: 'Switch Back', fr: 'Revenir', es: 'Volver' },
  people_profile_switch_back_confirm: {
    en: 'Are you sure you want to switch back to the old interface?',
    fr: "Voulez-vous vraiment revenir à l'ancienne interface?",
    es: '¿Está seguro de que desea volver a la interfaz anterior?',
  },

  // ---- Profile dialogs
  people_company_change_body: {
    en: 'You have an account in more than one company. Please select the one you would like to access.',
    fr: "Vous avez un compte dans plus d'une entreprise. Veuillez choisir celle à laquelle vous souhaitez accéder.",
    es: 'Tiene una cuenta en más de una empresa. Seleccione a la que desea acceder.',
  },
  people_password_title: { en: 'Change Your Password', fr: 'Changer votre mot de passe', es: 'Cambie su contraseña' },
  people_password_hint: {
    en: 'Please remember, this password will be needed for the app on your phone.',
    fr: "N'oubliez pas : ce mot de passe sera requis pour l'application sur votre téléphone.",
    es: 'Recuerde que necesitará esta contraseña para la aplicación de su teléfono.',
  },
  people_password_saving: { en: 'Saving Password...', fr: 'Enregistrement du mot de passe...', es: 'Guardando la contraseña...' },
  people_password_min: {
    en: 'Password must be at least 8 characters.',
    fr: 'Le mot de passe doit comporter au moins 8 caractères.',
    es: 'La contraseña debe tener al menos 8 caracteres.',
  },
  people_password_mismatch: { en: 'Passwords do not match.', fr: 'Les mots de passe ne correspondent pas.', es: 'Las contraseñas no coinciden.' },
  people_password_all_required: { en: 'All fields are required.', fr: 'Tous les champs sont obligatoires.', es: 'Todos los campos son obligatorios.' },
  people_password_new_min: {
    en: 'New password must be at least 8 characters.',
    fr: 'Le nouveau mot de passe doit comporter au moins 8 caractères.',
    es: 'La nueva contraseña debe tener al menos 8 caracteres.',
  },
  people_password_confirm_mismatch: {
    en: 'New password does not match the confirmation password.',
    fr: 'Le nouveau mot de passe ne correspond pas à la confirmation.',
    es: 'La nueva contraseña no coincide con la confirmación.',
  },
  people_signout_confirm: {
    en: 'Are you sure you want to sign out?',
    fr: 'Voulez-vous vraiment vous déconnecter?',
    es: '¿Está seguro de que desea cerrar sesión?',
  },
};
